<template>
    <div
        :data-type="context.type"
        :class="`formulate-input-element formulate-input-element--${context.classification}`"
    >
        <!--
            NOTE: need to implement today/clear buttons ourselves in default slot
            also need to implement our own validation state styling (but formulate will also handle some of that)
            today-button
            :label-today-button="`Today`"
            reset-button
            :label-reset-button="`Clear`"
            can also set width prop default is '270px'
            -->
        <b-calendar
            v-if="inline"
            key="showingInlineCalendar"
            :value="localValue"
            v-bind="passThroughAttrsProps"
            :value-as-date="true"
            :date-format-options="dateFormatOptions"
            :min="localMinDate"
            :max="localMaxDate"
            :disabled="localDisabled"
            block
            class="formulate-input-element-bootstrap-date-inline"
            @input="handleSelection"
        >
            <div class="d-flex" dir="ltr">
                <b-button
                    size="sm"
                    variant="light-danger"
                    v-if="localValue"
                    @click="handleClear"
                >
                    Clear
                </b-button>
                <b-button
                    size="sm"
                    variant="light-primary"
                    class="ml-auto"
                    @click="handleToday"
                >
                    Set Today
                </b-button>
            </div>
        </b-calendar>  <!-- can also set width prop default is '270px' -->
        <b-form-datepicker
            v-else
            key="showingPopupCalendar"
            :value="localValue"
            v-bind="passThroughAttrsProps"
            :value-as-date="true"
            :date-format-options="dateFormatOptions"
            :min="localMinDate"
            :max="localMaxDate"
            today-button
            :label-today-button="`Today`"
            reset-button
            no-flip
            :label-reset-button="`Clear`"
            :disabled="localDisabled"
            :state="visibleValidationState"
            @input="handleSelection"
            @hidden="handlePopupHidden"
        ></b-form-datepicker>   <!-- seems that @blur event is not emitted from b-form-datepicker -->
    </div>  <!-- :popper-opts="{ modifiers: { preventOverflow: { enabled: false, order: 300 } } }" -->
</template>  <!-- :no-flip="true" -->

<script>
import { DateTime } from 'luxon';

// NOTE: 
// NOTE: this component tries to stay as close to the standard FormulateDate as possible,
// but the formulate date component has confusing support/docs for min/max dates, sometimes using before/after instead
// before/after seems to be the better way to do it, but for backwards compatibility, we'll use both
// bootstrap_date will use before/after first, then fallback to min/max if those are set but min/max are not

// UPDATE Nov 17 2025
// the VueFormulate before/after validation rules will always fail if the value is empty, even if field is not required
// so we made special rules (optionalBefore and optionalAfter) that only fail if there is a value present that is parsed to a date
// if you also want required, then also use the required rule (set required=True)

const BEFORE_RULE_NAMES = ['before', 'optionalBefore'];
const AFTER_RULE_NAMES = ['after', 'optionalAfter'];

export default {
    name: 'FormulateBootstrapDate',
    components: {},
    mixins: [],
    props: {
        context: {
            // the required Formulate context prop
            type: Object,
            required: true,
        },
        inline: {
            // whether to show the datepicker as inline calendar or not
            type: Boolean,
            default: false,
        },
        componentProps: {
            // we pass this to the date picker component directly (with some things overridden/merged in)
            type: Object,
            default: () => ({}),
        },
        // NOTE: not adding props for min/max here -- using the built-in formulate rules for that
    },
    data() {
        return {
            localValue: null,  // our normalized value, its a JS Date object here!  (that's what the PrimeCalendar wants)
            defaultDateFormatOptions: {  // calling this the 'default' can override specific things in a computed using props
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                // weekday: 'long',  // not including this for now... kinda annoying
            },
        };
    },
    computed: {
        boundaryElement() { // NOTE: this does funky things... gotta test it with all the other options
            // we get the boundary element dynamically... should return an HTMLElement or strings of 'window', 'scrollParent', 'viewport'
            // we will NOT always be inside flow-object-detail.... so you may need to expand this more....
            const flowObjDetail = document.getElementsByClassName('flow-object-detail')[0];
            // NOTE - you could have multiple flowObjectDetail elements on the page, so this is not a good way to get it
            // const flowObjDetail = this.$el ? this.$el.closest('.flow-object-detail') : null;
            // could also try looking for ancestor of form.formulate-form
            // const ancestorForm = this.$el ? this.$el.closest('form.formulate-form') : null;
            // console.log('ancestorForm', ancestorForm);
            // const boundary = flowObjDetail || ancestorForm || 'window';
            const boundary = flowObjDetail ? flowObjDetail : 'window';  // this is the default for PrimeVue
            console.log('boundaryElement for FormulateBootstrapDate', boundary);
            return boundary;
        },
        passThroughAttrsProps() {
            // we merge the context.attributes with the componentProps... we want to v-bind both to pass through to our component, but we can't have v-bind for 2 things apparently
            // so we merge them here
            // we pull off min/max attrs... those may get passed in, but we handle them in our localMinDate/localMaxDate
            const otherAttrs = {};
            Object.keys(this.context.attributes).forEach(key => {
                if (key !== 'min' && key !== 'max') {
                    otherAttrs[key] = this.context.attributes[key];
                }
            });
            return Object.assign({}, otherAttrs, this.componentProps);
        },
        localValueText() {
            // return a string representation of the localValue - need to wrap in single quotes or else it gets interpolated by PrimeVue
            return this.localValue ? "'" + DateTime.fromJSDate(this.localValue).toLocaleString(DateTime.DATE_FULL) + "'" : '';
        },
        localDisabled() {
            return this.context.attributes?.disabled ?? this.context.disabled ?? false;
        },
        dateFormatOptions() {
            // merge the default options with any options passed in from the componentProps
            return Object.assign({}, this.defaultDateFormatOptions, this.componentProps?.dateFormatOptions ?? {});
        },
        visibleValidationState() {
            // passed to the :state prop of the b-form-datepicker - shows the state based on the formulate context
            // just for a better display of invalid state
            // NOT showing 'valid' state, since other formulate components do not do this
            // seems the best thing to look at in context is 'visibleValidationErrors' -- an array, length of 0 if all is OK or they are NOT supposed to show yet
            const shouldShowInvalid = false;  // Array.isArray(this.context.visibleValidationErrors) && this.context.visibleValidationErrors.length > 0;
            // but never show invalid if we are disabled
            return shouldShowInvalid && !this.localDisabled ? false : null;  // only show invalid state, not valid state
        },
        localMinDate() {
            // we instrospect the vue formulate context to pull out the min date from the rule and/or get the min attribute if passed in 
            // we cannot just rely on passing down the min attribute, because that only blocks the selection of the date, it doesn't actually validate the date correctly
            // if we are working with Date() objects locally with the b-form-datepicker, we need to convert the min date to a Date object
            // if we are working with ISO strings, we can just pass it through
            // NOTE: the formulate docs are confusing about min/max vs before/after - we will honor before/after first, then min/max only if before/after are not set
            // NOTE: 'after' is EXCLUSIVE, 'min' is INCLUSIVE (both in formulate and in bootstrap component) -- need to adjust accordingly
            const afterRule = this.context.rules?.find(r => AFTER_RULE_NAMES.includes(r.ruleName)) ?? null;
            const minRule = this.context.rules?.find(r => r.ruleName === 'min') ?? null;
            const minAttr = this.context.attributes?.min ?? null;
            let ruleDate = null;
            let adjustDays = 0;
            if (afterRule) {
                ruleDate = afterRule.args[0];
                adjustDays = 1;  // if you used 'after' correctly, then our actual min date is the day AFTER this date (since min is inclusive)
            } else if (minRule) {
                ruleDate = minRule.args[0];
                adjustDays = 0;
            } else if (minAttr) {
                ruleDate = minAttr;
                adjustDays = 0;
            }
            if (ruleDate) {
                const luxonDate = DateTime.fromISO(ruleDate);
                return luxonDate.isValid ? luxonDate.plus({days: adjustDays}).toJSDate() : null;
            }
            return null;
        },
        localMaxDate() {
            // we instrospect the vue formulate context to pull out the max date from the rule, if set -- so we don't have 2 separate rules being set
            // if we are working with Date() objects locally with the b-form-datepicker, we need to convert the min date to a Date object
            // if we are working with ISO strings, we can just pass it through
            const beforeRule = this.context.rules?.find(r => BEFORE_RULE_NAMES.includes(r.ruleName)) ?? null;
            const maxRule = this.context.rules?.find(r => r.ruleName === 'max') ?? null;
            const maxAttr = this.context.attributes?.max ?? null;
            let ruleDate = null;
            let adjustDays = 0;  // if you used 'before' correctly, then the actual max date is the day BEFORE this date (since max is inclusive)
            if (beforeRule) {
                ruleDate = beforeRule.args[0];
                adjustDays = -1;
            } else if (maxRule) {
                ruleDate = maxRule.args[0];
                adjustDays = 0;
            } else if (maxAttr) {
                ruleDate = maxAttr;
                adjustDays = 0;
            }
            if (ruleDate) {
                const luxonDate = DateTime.fromISO(ruleDate);
                return luxonDate.isValid ? luxonDate.plus({days: adjustDays}).toJSDate() : null;
            }
            return null;
        },
    },
    watch: {
        'context.model': {
            handler(newVal, oldVal) {
                // handle changes to the model value in formulate form, set our localValue
                // will also get called when we set the model value... so need to diff things
                
                console.log('model changed: new, old =', newVal === '' ? 'EMPTY_STRING' : newVal, oldVal);
                const emptyValue = null;
                // if (newVal === this.localValue) {
                //     return;  // no change
                // }
                if (newVal === null || newVal === undefined || newVal === '') {
                    //this.localValue = emptyValue;
                    this.handleSelection(emptyValue);  // this will set the localValue and update the context.model
                } else {
                    const luxonDT = DateTime.isDateTime(newVal) ? newVal : DateTime.fromISO(newVal);  // should not be a luxon DT, but just in case
                    //this.localValue = luxonDT.isValid ? luxonDT.toJSDate() : emptyValue;
                    const newSelection = luxonDT.isValid ? luxonDT.toJSDate() : emptyValue;
                    this.handleSelection(newSelection);  // this will set the localValue and update the context.model
                }
                // no need to 'emit' here... there is no 'emit'... only setting context.model, but that's what this is for, to watch that
            },
            immediate: true,  // this seems to not match what other components do... so no need?
        },
    },
    created() {
        
    },
    mounted() {
        
    },
    methods: {
        handleClear() {
            // handle the clear event
            // payload here is null
            console.log('clear payload');
            this.handleSelection(null);
        },
        handleToday() {
            // handle the today event
            // payload here is a JS Date object
            console.log('today payload');
            this.handleSelection(new Date());
        },
        handleSelection(jsDate) {
            // handle the selection of a date (not any change, just the user-selection, right?)
            // THIS DOES NOT GET UPDATED IF SOMEONE MANUALLY CHANGES THE INPUT FIELD... is that what you want?
            // payload here is a JS Date object
            // set this.context.model to the selected date
            console.log('selection payload jsDate', jsDate);
            // don't set localValue here, just let it get updated in our watcher???? no, we'll update here to handle it like normal v-model
            if (jsDate !== this.localValue) {
                this.localValue = jsDate;
            }
            // this.localValue = jsDate; // JS Date object or null
            const isoDate = jsDate ? DateTime.fromJSDate(jsDate).toISODate() : '';  // WHAT ABOUT TIMES?  should be OK since it's all local here?
            // only set the context model if it's different (should we ignore changes to 'empty' values?  such as changing from undefined to null or ''?  seems useful and might solve legit issue)
            const modelHasValue = this.context.model !== null && this.context.model !== undefined && this.context.model !== '';
            const selectionHasValue = isoDate !== null && isoDate !== undefined && isoDate !== '';
            /// btw... I'm sure there is a better way to do this, but this is clear... 
            if (modelHasValue !== selectionHasValue || (modelHasValue && selectionHasValue && isoDate !== this.context.model)) {
                this.context.model = isoDate;
                // gotta trigger blur event IN NEXT TICK, otherwise it seems to miss the change detection in parent (at least when in a group)
                // only call the blur stuff when you know you changed something
                this.$nextTick(() => {
                    this.$emit('blur');
                    this.context.blurHandler();
                });
            }
            // if (isoDate !==  this.context.model) {
            //     this.context.model = isoDate;
            //     // gotta trigger blur event IN NEXT TICK, otherwise it seems to miss the change detection in parent (at least when in a group)
            //     // only call the blur stuff when you know you changed something
            //     this.$nextTick(() => {
            //         this.$emit('blur');
            //         this.context.blurHandler();
            //     });
            // }
            // this.$emit('blur');  // emit the input event so form machinery works (important for group fields, etc.... or else the model gets updated, but side-effects do not get run)
            // this.context.blurHandler();  // we must call this here to trigger validation to update, errors to be shown
        },
        handlePopupHidden() {
            // trigger blurHandler here also?
            console.log('datepicker popup hidden');
            // don't need this here... at least not if we do it in selection handler
            // this.$nextTick(() => {
            //     this.$emit('blur');
            //     this.context.blurHandler();
            // });
            // this.$emit('blur');
            // this.context.blurHandler();
        },
        handleCalendarContextEvent(payload) {
            // handle the context event from the calendar component or datepicker (not used now)
            console.log('calendar context event', payload);
            // this seems to fire with some delay... but we get a lot of stuff in there
            // but we probably don't need it... @input should do the job
            // this.$emit('context', payload);
        },
    },
}
</script>

<style lang="scss">
    .formulate-input-element-bootstrap-date-inline .b-calendar-header > output {
        // make the text in the 'current selection' area a bit bigger
        font-size: 1rem;
        font-weight: 500;
    }
</style>